/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarTask,
  CalendarTaskHeaderGroup,
  CalendarTasksGroup,
  CommonUtils,
  CustomDatePipe,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { CalendarView } from '../calendar-view';
import { CalendarHorizontalResourcesComponent } from '../calendar-horizontal-resources/calendar-horizontal-resources.component';
import dayjs from 'dayjs';
import { CalendarSection } from './calendar-section';
import { TranslateService } from '@ngx-translate/core';
import { NgClass, UpperCasePipe } from '@angular/common';
import { CalendarSectionComponent } from './calendar-section/calendar-section.component';
import { CalendarResourceLine } from '../calendar-resource-line';
import { Telework } from '../telework';
import { DayNotWorkService } from '../day-not-work.service';
import { TeleworkService } from '../telework.service';

@Component({
  selector: 'infl-calendar-view-day-horizontal',
  templateUrl: './calendar-view-day-horizontal.component.html',
  styleUrls: [
    './calendar-view-day-horizontal.component.scss',
    '../compatibility.scss',
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CalendarHorizontalResourcesComponent,
    NgClass,
    UpperCasePipe,
    CalendarSectionComponent,
    CustomDatePipe,
  ],
})
export class CalendarViewDayHorizontalComponent
  implements OnInit, CalendarView
{
  public resources = input<CalendarTaskHeaderGroup[]>();
  public showResources = input(false);
  public startMorning = input({ hours: 7, minutes: 0 });
  public endMorning = input({ hours: 12, minutes: 0 });
  public startAfternoon = input({ hours: 13, minutes: 0 });
  public endAfternoon = input({ hours: 18, minutes: 0 });
  public outsideDraggingTask = input<{ task: CalendarTask; shift: number }>();
  public dragByOutside = input<CalendarDropTask<any>>();
  public displayTime = input(true);
  public canDrag = input(true);
  public canResize = input(true);
  public canSelect = input(true);
  public tasks = input<CalendarTask[]>();
  public resourceLines = input<CalendarResourceLine[]>();
  public onWeek = input(false);
  public day = input<Date>();
  public sectionHeight = input(40);
  public taskHeight = input(24);
  public enable = input(true);
  public resourceTitle = input<string>();
  public cellWidth = input<'auto' | number>();
  public numberSection = input.required<1 | 2>();
  public teleworks = input<Telework[]>(null);

  public hourSelection = output<WeekSelectionEvent>();
  public hourPreselection = output<WeekSelectionEvent>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<{
    date: Date;
    group: { header: string; group: string };
  }>();
  public taskClick = output<CalendarClickEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public clickOnSelection = output<WeekSelectionEvent>();
  public dblClickOnSelection = output<WeekSelectionEvent>();
  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output();
  public taskHover = output<CalendarTask>();

  protected sectionsComponents =
    viewChildren<CalendarSectionComponent>('section');

  public isCurrentDay: boolean;
  public draggingTask = signal<CalendarTask>(null);
  public height = signal<number>(null);

  public readonly translate = inject(TranslateService);
  public readonly teleworkService = inject(TeleworkService);
  public readonly dayNotWorkService = inject(DayNotWorkService);

  public backgroundColorDay = computed(() => {
    if (this.day() && !this.dayNotWorkService.isDayWork(this.day())) {
      return this.dayNotWorkService.dayNotWorkColor;
    }
    return 'var(--white)';
  });

  public weekNumber = computed(() => {
    return this.dateWeek(this.day());
  });

  public isToday = computed(() => {
    return CommonUtils.isToday(this.day());
  });

  public sections = computed(() => {
    const sections = {};
    this.resources().forEach((group) => {
      group.tasksGroups.forEach((subGroup) => {
        const calendarSection = new CalendarSection(
          this.numberSection(),
          group,
          subGroup,
        );
        calendarSection.startMorning = this.startMorningDate();
        calendarSection.endMorning = this.endMorningDate();
        calendarSection.startAfternoon = this.startAfternoonDate();
        calendarSection.endAfternoon = this.endAfternoonDate();
        this.checkTeleworkAndDayOff(calendarSection);
        sections[subGroup.id] = calendarSection;
      });
    });
    Object.keys(sections).forEach((key) => {
      const section: CalendarSection = sections[key];
      if (this.numberSection() === 2) {
        section.section2 = this.getTasksBySection(section.resource, 2);
        section.section1Overflow = this.getOverflowedTasks(section.resource);
      }
      section.section1 = this.getTasksBySection(section.resource, 1);
    });
    this.clearLastSectionHighlighted();
    return sections;
  });

  private startMorningDate = computed(() => {
    return dayjs(this.day())
      .clone()
      .hour(this.startMorning().hours)
      .minute(this.startMorning().minutes);
  });

  private endMorningDate = computed(() => {
    return dayjs(this.day())
      .clone()
      .hour(this.endMorning().hours)
      .minute(this.endMorning().minutes);
  });

  private startAfternoonDate = computed(() => {
    return dayjs(this.day())
      .clone()
      .hour(this.startAfternoon().hours)
      .minute(this.startAfternoon().minutes);
  });

  private endAfternoonDate = computed(() => {
    return dayjs(this.day())
      .clone()
      .hour(this.endAfternoon().hours)
      .minute(this.endAfternoon().minutes);
  });

  private lastSectionHighLighted: HTMLDivElement;

  constructor() {
    effect(() => {
      this.draggingTask.set(null);
      if (this.outsideDraggingTask()) {
        this.draggingTask.set(this.outsideDraggingTask().task);
      }
    });
    effect(() => {
      if (this.sections() && this.taskHeight()) {
        this.computeSubGroupHeight();
      }
    });
  }

  ngOnInit() {
    this.isCurrentDay = CommonUtils.compareDate(this.day(), new Date());
  }

  private checkTeleworkAndDayOff(section: CalendarSection) {
    section.isOff = !this.dayNotWorkService.isDayWork(
      section.startMorning.toDate(),
    );
    section.dayOffColor = this.dayNotWorkService.dayNotWorkColor;
    if (this.teleworks() && this.teleworks().length > 0) {
      section.isTeleworked = !!this.teleworks().find((telework) => {
        return (
          telework.resourceId === section.resource.id &&
          telework.date ===
            CommonUtils.convertDateToString(section.startMorning.toDate())
        );
      });
      section.teleworkColor = this.teleworkService.teleworkColor;
    }
  }

  private getOverflowedTasks(subGroup: CalendarTasksGroup) {
    const tasks = subGroup.tasks.filter((task) => {
      return task.dateStart.isSame(this.day(), 'day');
    });
    const isOverflowed = (task) => {
      return (
        task.dateStart.isSameOrBefore(this.endMorningDate()) &&
        task.dateEnd.isAfter(this.endMorningDate())
      );
    };
    return tasks.filter((task) => isOverflowed(task));
  }

  private getTasksBySection(
    subGroup: CalendarTasksGroup,
    section: 1 | 2,
  ): CalendarTask[] {
    const tasks = subGroup.tasks.filter((task) => {
      return task.dateStart.isSame(this.day(), 'day');
    });
    const isMorning = (task) => {
      return task.dateStart.isSameOrBefore(this.endMorningDate());
    };
    const isAfternoon = (task) => {
      return task.dateStart.isAfter(this.endMorningDate());
    };
    if (this.numberSection() === 2 && section === 1) {
      return tasks.filter((task) => isMorning(task));
    }
    if (this.numberSection() === 2 && section === 2) {
      return tasks.filter((task) => isAfternoon(task));
    }
    return tasks;
  }

  private computeSubGroupHeight() {
    this.resources().forEach((group) => {
      group.tasksGroups.forEach((subGroup) => {
        let height = 0;
        if (this.numberSection() === 1) {
          height =
            (this.taskHeight() + 5) *
            this.sections()[subGroup.id].section1.length;
        } else {
          height =
            (this.sectionHeight() + 5) *
            Math.max(
              this.sections()[subGroup.id].section1.length,
              this.sections()[subGroup.id].section2.length +
                this.sections()[subGroup.id].section1Overflow.length,
            );
        }
        if (height === 0) {
          height = this.sectionHeight() + 5;
        }
        if (!this.onWeek() || height > subGroup.height) {
          subGroup.height = height;
        }
      });
    });
  }

  public onDragStart(task: CalendarTask) {
    this.draggingTask.set(task);
    this.dragTask.emit({ task: task, shift: 0 });
  }

  public onDragLeaveTbody() {
    this.clearLastSectionHighlighted();
  }

  private clearLastSectionHighlighted() {
    if (this.lastSectionHighLighted) {
      this.lastSectionHighLighted.style.backgroundColor = '';
      this.lastSectionHighLighted = null;
    }
  }

  public sectionDragOver(event: DragEvent) {
    this.clearLastSectionHighlighted();
    const section = event.target as HTMLDivElement;
    this.lastSectionHighLighted = section;
    section.style.backgroundColor = '#FFF2D4';
  }

  public taskDropped() {
    this.draggingTask.set(null);
    this.dropOutside.emit();
    this.clearLastSectionHighlighted();
  }

  public dateWeek(date: Date): string {
    const tempDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const day = tempDate.getUTCDay() || 7;
    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil(
      ((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
    );
    return weekNumber < 10 ? '0' + weekNumber : '' + weekNumber;
  }

  findTask(id: string): { style: any; task: CalendarTask } {
    return { style: undefined, task: undefined };
  }

  generateTasks() {}

  getScrollPosition(): { x: number; y: number } {
    return { x: 0, y: 0 };
  }

  scrollToDate(date: Date): void {}

  setScrollPosition(x: number, y: number) {}

  onMouseMove($event: MouseEvent) {}

  public toPdf() {}

  deselect(): void {
    this.sectionsComponents().forEach((section) => section.deselect());
  }
}
